import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 12000 });

// Mirror of backend compute_quote() so the estimator always returns a number,
// even if the API is momentarily unavailable (source of truth remains the server).
export function computeQuoteLocal({ age, coverage, term, smoker = false, gender = "other" }) {
  const units = coverage / 1000;
  const ageFactor = 1 + Math.max(0, age - 25) * 0.035;
  const termFactor = 1 + (term - 10) * 0.012;
  const smokerFactor = smoker ? 1.6 : 1;
  const genderFactor = String(gender).toLowerCase() === "female" ? 0.92 : 1;
  let monthly = 0.09 * units * ageFactor * termFactor * smokerFactor * genderFactor;
  monthly = Math.max(monthly, 6);
  const market = monthly / 0.78;
  const round = (n) => Math.round(n * 100) / 100;
  const m = round(monthly);
  const mk = round(market);
  return {
    monthly_premium: m,
    annual_premium: round(m * 12),
    average_market_premium: mk,
    monthly_savings: round(mk - m),
    annual_savings: round((mk - m) * 12),
    coverage,
    term,
  };
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Resilient quote: try the API (with one retry), fall back to local compute on failure.
export async function getQuote(payload) {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { data } = await api.post("/quote", payload);
      return data;
    } catch (e) {
      if (attempt === 0) {
        await sleep(600);
        continue;
      }
    }
  }
  return computeQuoteLocal(payload);
}

export const createLead = (payload) => api.post("/leads", payload).then((r) => r.data);

export const createMeetingBooking = (payload) =>
  api.post("/meeting-bookings", payload).then((r) => r.data);

export const fetchMeetingBookings = () =>
  api.get("/meeting-bookings", authHeader()).then((r) => r.data);

export const deleteMeetingBooking = (id) =>
  api.delete(`/meeting-bookings/${id}`, authHeader()).then((r) => r.data);

// ---- Google Calendar OAuth (admin) ----
export const fetchGcalStatus = () =>
  api.get("/oauth/calendar/status", authHeader()).then((r) => r.data);

export const startGcalOAuth = () =>
  api.get("/oauth/calendar/login", authHeader()).then((r) => r.data);

export const disconnectGcal = () =>
  api.delete("/oauth/calendar", authHeader()).then((r) => r.data);
export const getStats = () => api.get("/stats").then((r) => r.data);

// ---- Admin auth ----
const TOKEN_KEY = "alp_admin_token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export const adminLogin = (email, password) =>
  api.post("/auth/login", { email, password }).then((r) => r.data);

export const fetchMe = () => api.get("/auth/me", authHeader()).then((r) => r.data);

export const fetchLeads = () => api.get("/leads", authHeader()).then((r) => r.data);
export const deleteLead = (id) => api.delete(`/leads/${id}`, authHeader()).then((r) => r.data);
export const rateLead = (id, rating) =>
  api.patch(`/leads/${id}/rating`, { rating }, authHeader()).then((r) => r.data);

// ---- Applications ----
export const submitApplication = (payload) =>
  api.post("/applications", payload).then((r) => r.data);

// Short (Typeform-style) application — multipart with optional policy file.
export const submitShortApplication = (formData) =>
  api.post("/short-applications", formData, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then((r) => r.data);

export const fetchShortApplications = () =>
  api.get("/short-applications", authHeader()).then((r) => r.data);

export const deleteShortApplication = (id) =>
  api.delete(`/short-applications/${id}`, authHeader()).then((r) => r.data);
export const rateShortApplication = (id, rating) =>
  api.patch(`/short-applications/${id}/rating`, { rating }, authHeader()).then((r) => r.data);

export const downloadShortApplicationPolicy = async (id, filename) => {
  const res = await api.get(`/short-applications/${id}/policy`, { ...authHeader(), responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `advanced-life-policy-${id}`;
  a.click();
  URL.revokeObjectURL(url);
};

// ---- Notes ----
export const fetchNotes = (entityType, entityId) =>
  api.get(`/notes`, { ...authHeader(), params: { entity_type: entityType, entity_id: entityId } }).then((r) => r.data);

export const createNote = (entityType, entityId, body) =>
  api.post(`/notes`, { entity_type: entityType, entity_id: entityId, body }, authHeader()).then((r) => r.data);

export const deleteNote = (id) => api.delete(`/notes/${id}`, authHeader()).then((r) => r.data);

export const fetchNoteCounts = (entityType) =>
  api.get(`/notes/counts`, { ...authHeader(), params: { entity_type: entityType } }).then((r) => r.data);
